import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Chip, PageHeader, PageLoading, Switch, Textarea, useToast } from '@koyomi/ui';
import { api, ApiError } from '../../lib/api.ts';
import { useAuthStore } from '../../app/auth-store.ts';
import type { WizardView } from '@koyomi/shared';
import './setup-wizard.css';

type Scope = 'business' | 'team' | 'ai' | 'scheduling' | 'services' | 'hours';
const SCOPE_ORDER: Scope[] = ['business', 'services', 'hours', 'team', 'ai', 'scheduling'];
type Faq = { question: string; answer: string };
interface AiPart { assistantName?: string; tone?: string; systemPrompt?: string; mustKnow?: string[]; mustNotDo?: string[]; faq?: Faq[]; outOfHoursMessage?: string; contingencyMessage?: string; handoffBehavior?: string; appointmentNotesRules?: string }
interface BusinessPart { name?: string; segment?: string | null; address?: string | null; phone?: string | null; timezone?: string; modality?: string }
type MemberHours = Record<string, { intervals: { start: string; end: string }[] } | null> | null;
interface TeamItem { memberId?: string; name: string; email?: string; role?: string; services?: string[]; workingHours?: MemberHours; canBeSubstitute?: boolean }
interface TeamCurrent { memberId: string; name: string; email: string; role: string; services: string[]; workingHours: MemberHours; canBeSubstitute: boolean; pending?: boolean }
interface ServicePart { id?: string; name: string; description?: string | null; durationMin?: number; price?: number | null; priceVisibility?: string; bufferBeforeMin?: number; bufferAfterMin?: number }
type DayHours = { open: boolean; intervals: { start: string; end: string }[]; lunch: { start: string; end: string } | null };
interface Proposal { business?: BusinessPart; team?: TeamItem[]; ai?: AiPart; scheduling?: Record<string, unknown>; services?: ServicePart[]; hours?: Record<string, DayHours> }
interface Current { business: BusinessPart; team: TeamCurrent[]; ai: AiPart; scheduling: Record<string, unknown>; services: ServicePart[]; hours: Record<string, DayHours> }
export interface WizardSession { id: string; scopes: Scope[]; status: 'OPEN' | 'APPLIED'; messages: { role: 'user' | 'assistant'; text: string; at: string }[]; proposal: Proposal | null; current: Current; appliedParts: string[]; createdAt: string; userMessages: number; maxUserMessages: number }
export interface WizardData { wizard: WizardView; session: WizardSession | null; maxUserMessages: number }
interface ApplyResult { wizard: WizardView; session: WizardSession; applied: string[]; skipped: { part: string; reason: string }[]; warnings: { part: string; reason: string }[] }

const DAYS = ['1', '2', '3', '4', '5', '6', '0'];
const DOW = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
export const wizardKey = () => [useAuthStore.getState().businessId, 'setup-wizard'] as const;
export const useSetupWizard = (enabled = true) => useQuery({ queryKey: wizardKey(), queryFn: () => api<WizardData>('/business/setup-wizard'), enabled });

/**
 * Wizard de primeira configuração (roadmap §1): chat com o assistente no lugar da Ativação enquanto a janela está aberta.
 * Proposta aparece como diff (atual riscado, proposto em destaque), por parte, com "aplicar". Sem custo em R$: só o aviso de cota.
 */
export function SetupWizardPage({ onDone }: { onDone?: () => void } = {}) {
  const { t, i18n } = useTranslation(['config', 'common']);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const data = useSetupWizard();
  const [session, setSession] = useState<WizardSession | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState<null | 'start' | 'send' | 'apply' | 'skip'>(null);
  const [parts, setParts] = useState<string[]>([]);
  const [skipped, setSkipped] = useState<{ part: string; reason: string }[]>([]);
  const [warnings, setWarnings] = useState<{ part: string; reason: string }[]>([]);
  const [confirmSkip, setConfirmSkip] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const invalidate = () => qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === useAuthStore.getState().businessId });

  useEffect(() => { if (data.data?.session && !session) setSession(data.data.session); }, [data.data, session]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [session?.messages.length, busy]);
  const partsKey = useRef<string | null>(null);
  useEffect(() => {
    if (!session?.proposal) { partsKey.current = null; return; }
    const next = proposalParts(session.proposal);
    const k = `${session.id}:${next.join(',')}`;
    if (partsKey.current === k) return;
    partsKey.current = k;
    setParts(next.filter((p) => !session.appliedParts.includes(p)));
  }, [session?.id, session?.proposal, session?.appliedParts]);

  const call = async <T,>(kind: NonNullable<typeof busy>, fn: () => Promise<T>): Promise<T | null> => {
    setBusy(kind);
    try { return await fn(); } catch (err) { toast.push(err instanceof ApiError ? err.message : t('common:errors.INTERNAL'), 'danger'); return null; } finally { setBusy(null); }
  };
  const start = async () => {
    const r = await call('start', () => api<WizardData>('/business/setup-wizard/session', { body: {} }));
    if (r?.session) setSession(r.session);
  };
  const send = async () => {
    if (!session || !text.trim()) return;
    const msg = text.trim(); setText('');
    setSession({ ...session, messages: [...session.messages, { role: 'user', text: msg, at: new Date().toISOString() }] });
    const r = await call('send', () => api<WizardData>('/business/setup-wizard/messages', { body: { sessionId: session.id, text: msg } }));
    if (r?.session) { setSession(r.session); void qc.invalidateQueries({ queryKey: [useAuthStore.getState().businessId, 'business'] }); } else setText(msg);
  };
  const apply = async () => {
    if (!session || !parts.length || !confirm(t('config:onboarding.wizard.applyConfirm'))) return;
    const r = await call('apply', () => api<ApplyResult>('/business/setup-wizard/apply', { body: { sessionId: session.id, parts } }));
    if (r) {
      setSession(r.session); setSkipped(r.skipped); setWarnings(r.warnings);
      await invalidate();
      toast.push(r.skipped.length ? t('config:onboarding.wizard.appliedPartial', { n: r.applied.length }) : t('config:onboarding.wizard.applied'), r.skipped.length ? 'warn' : 'success');
      if (r.applied.length && r.wizard.status === 'DONE') onDone?.();
    }
  };
  const skip = async (final: boolean) => {
    const r = await call('skip', () => api<{ wizard: WizardView }>('/business/setup-wizard/skip', { body: { final } }));
    if (!r) return;
    await invalidate();
    if (final) onDone?.(); else navigate('/agenda');
  };

  if (data.isLoading) return <><PageHeader title={t('config:onboarding.wizard.title')} /><PageLoading cards={2} rows={6} /></>;
  const d = data.data;
  if (!d) return null;
  const limitReached = session ? session.userMessages >= session.maxUserMessages : false;
  const header = <PageHeader title={t('config:onboarding.wizard.title')} subtitle={t('config:onboarding.wizard.subtitle')} actions={<span className="k-row" style={{ gap: 8, flexWrap: 'wrap' }}><Button size="sm" variant="ghost" loading={busy === 'skip'} onClick={() => void skip(false)}>{t('config:onboarding.wizard.later')}</Button><Button size="sm" variant="ghost" onClick={() => setConfirmSkip(true)}>{t('config:onboarding.wizard.never')}</Button></span>} />;
  const quotaNote = <div className="swz__quota" role="note">{t('config:onboarding.wizard.quotaNote')}</div>;

  if (confirmSkip) {
    return (
      <>
        {header}
        <Card title={t('config:onboarding.wizard.neverTitle')} subtitle={t('config:onboarding.wizard.neverHint')} style={{ maxWidth: 640 }}>
          <div className="k-row" style={{ gap: 8 }}><Button onClick={() => setConfirmSkip(false)}>{t('common:actions.cancel')}</Button><Button variant="primary" loading={busy === 'skip'} onClick={() => void skip(true)}>{t('config:onboarding.wizard.neverConfirm')}</Button></div>
        </Card>
      </>
    );
  }

  if (!session) {
    return (
      <>
        {header}
        <Card title={t('config:onboarding.wizard.introTitle')} subtitle={t('config:onboarding.wizard.introHint')} style={{ maxWidth: 720 }}>
          <div className="k-stack" style={{ gap: 14 }}>
            <ul className="swz__intro-list">{(['services', 'hours', 'team', 'ai'] as const).map((k) => <li key={k}>{t(`config:onboarding.wizard.covers.${k}`)}</li>)}</ul>
            {quotaNote}
            <Button variant="primary" loading={busy === 'start'} onClick={() => void start()} style={{ alignSelf: 'flex-start' }}>{t('config:onboarding.wizard.start')}</Button>
          </div>
        </Card>
      </>
    );
  }

  const p = session.proposal;
  const cur = session.current;
  const partLabel = (k: string) => t(`config:onboarding.wizard.parts.${k}`, { defaultValue: k });
  return (
    <>
      {header}
      {quotaNote}
      <div className="swz">
        <Card className="swz__chat" padded={false}>
          <div className="swz__messages">
            {session.messages.map((m, i) => <div key={i} className={`swz__msg swz__msg--${m.role}`}>{m.text}</div>)}
            {busy === 'send' && <div className="swz__msg swz__msg--assistant swz__typing"><span /><span /><span /></div>}
            <div ref={endRef} />
          </div>
          <div className="swz__compose">
            <Textarea rows={3} value={text} placeholder={limitReached ? t('config:onboarding.wizard.limitReached') : t('config:onboarding.wizard.placeholder')} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void send(); }} disabled={limitReached || busy === 'send'} />
            <div className="k-row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <span className="k-muted" style={{ fontSize: 12 }}>{t('config:onboarding.wizard.messagesCount', { n: session.userMessages, max: session.maxUserMessages })}</span>
              <Button variant="primary" loading={busy === 'send'} disabled={!text.trim() || limitReached} onClick={() => void send()}>{t('config:onboarding.wizard.send')}</Button>
            </div>
          </div>
        </Card>
        <Card className="swz__proposal" title={t('config:onboarding.wizard.proposal')} subtitle={p ? t('config:onboarding.wizard.proposalHint') : t('config:onboarding.wizard.noProposal')}>
          {p && (
            <div className="k-stack" style={{ gap: 18 }}>
              {p.business && <Part id="business" label={partLabel('business')} parts={parts} setParts={setParts} applied={session.appliedParts}>
                {(['name', 'segment', 'phone', 'address', 'timezone', 'modality'] as const).map((k) => p.business![k] !== undefined && <Diff key={k} label={t(`config:onboarding.wizard.fields.${k}`)} before={fmt(cur.business[k])} after={k === 'modality' ? t(`config:onboarding.wizard.modality.${p.business![k]}`, { defaultValue: String(p.business![k]) }) : fmt(p.business![k])} />)}
              </Part>}
              {p.services && <Part id="services" label={partLabel('services')} parts={parts} setParts={setParts} applied={session.appliedParts}>
                {p.services.map((s, i) => { const before = s.id ? cur.services.find((x) => x.id === s.id) : cur.services.find((x) => x.name.toLowerCase() === s.name.toLowerCase()); return <Diff key={s.id ?? `new${i}`} label={before ? s.name : `${s.name} · ${t('config:onboarding.wizard.newItem')}`} before={before ? serviceText(before, i18n.language) : undefined} after={serviceText(s, i18n.language)} long />; })}
              </Part>}
              {p.hours && <Part id="hours" label={partLabel('hours')} parts={parts} setParts={setParts} applied={session.appliedParts}>
                {DAYS.map((dk) => <Diff key={dk} label={new Intl.DateTimeFormat(i18n.language, { weekday: 'long' }).format(new Date(Date.UTC(2026, 8, 6 + Number(dk), 12)))} before={dayText(cur.hours[dk], t('config:onboarding.wizard.closed'), t('config:onboarding.wizard.lunch'))} after={dayText(p.hours![dk], t('config:onboarding.wizard.closed'), t('config:onboarding.wizard.lunch'))} />)}
              </Part>}
              {p.team && <Part id="team" label={partLabel('team')} parts={parts} setParts={setParts} applied={session.appliedParts}>
                {p.team.map((m, i) => { const before = m.memberId ? cur.team.find((x) => x.memberId === m.memberId) : cur.team.find((x) => m.email && x.email.toLowerCase() === m.email.toLowerCase()); return <Diff key={m.memberId ?? m.email ?? i} label={before ? before.name : `${m.name} · ${t('config:onboarding.wizard.invite')}`} before={before ? teamText(before, t) : undefined} after={teamText(m, t)} long />; })}
              </Part>}
              {p.ai && <Part id="ai" label={partLabel('ai')} parts={parts} setParts={setParts} applied={session.appliedParts}>
                {(['assistantName', 'tone', 'handoffBehavior'] as const).map((k) => p.ai![k] !== undefined && <Diff key={k} label={t(`config:onboarding.wizard.fields.${k}`)} before={cur.ai[k] as string | undefined} after={p.ai![k] as string} />)}
                {p.ai.systemPrompt !== undefined && <Diff label={t('config:onboarding.wizard.fields.systemPrompt')} before={cur.ai.systemPrompt} after={p.ai.systemPrompt} long />}
                {(['mustKnow', 'mustNotDo'] as const).map((k) => p.ai![k] !== undefined && <ListDiff key={k} label={t(`config:onboarding.wizard.fields.${k}`)} before={cur.ai[k] ?? []} after={p.ai![k]!} />)}
                {p.ai.faq !== undefined && <ListDiff label={t('config:onboarding.wizard.fields.faq')} before={(cur.ai.faq ?? []).map((f) => `${f.question}\n${f.answer}`)} after={p.ai.faq.map((f) => `${f.question}\n${f.answer}`)} />}
                {(['appointmentNotesRules', 'outOfHoursMessage', 'contingencyMessage'] as const).map((k) => p.ai![k] !== undefined && <Diff key={k} label={t(`config:onboarding.wizard.fields.${k}`)} before={cur.ai[k]} after={p.ai![k]!} long />)}
              </Part>}
              {p.scheduling && <Part id="scheduling" label={partLabel('scheduling')} parts={parts} setParts={setParts} applied={session.appliedParts}>
                {Object.entries(p.scheduling).map(([k, v]) => <Diff key={k} label={t(`config:onboarding.wizard.fields.${k}`, { defaultValue: k })} before={fmt(cur.scheduling[k])} after={fmt(v)} />)}
              </Part>}
              {warnings.length > 0 && <div className="swz__warn" role="status">{warnings.map((w) => <div key={w.part}><strong>{partLabel(w.part)}:</strong> {w.reason}</div>)}</div>}
              {skipped.length > 0 && <div className="swz__skipped" role="alert">{skipped.map((s) => <div key={s.part}><strong>{partLabel(s.part)}:</strong> {s.reason}</div>)}</div>}
              <Button variant="primary" disabled={!parts.some((x) => !session.appliedParts.includes(x))} loading={busy === 'apply'} onClick={() => void apply()}>{t('config:onboarding.wizard.apply')}</Button>
              <p className="k-muted" style={{ fontSize: 12, margin: 0 }}>{t('config:onboarding.wizard.applyHint')}</p>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

function proposalParts(p: Proposal): string[] { return SCOPE_ORDER.filter((k) => p[k] !== undefined); }
const fmt = (v: unknown) => (v === undefined || v === null || v === '' ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));
function serviceText(s: ServicePart, locale: string): string {
  const price = s.price == null ? '—' : new Intl.NumberFormat(locale, { style: 'currency', currency: 'BRL' }).format(s.price);
  return [`${s.durationMin ?? '—'} min · ${price} (${s.priceVisibility ?? 'SHOW'}) · ${s.bufferBeforeMin ?? 0}/${s.bufferAfterMin ?? 0} min`, s.description ?? ''].filter(Boolean).join('\n');
}
function dayText(d: DayHours | undefined, closed: string, lunch: string): string {
  if (!d || !d.open || !d.intervals.length) return closed;
  return `${d.intervals.map((i) => `${i.start}–${i.end}`).join(', ')}${d.lunch ? ` · ${lunch} ${d.lunch.start}–${d.lunch.end}` : ''}`;
}
function teamText(m: TeamItem | TeamCurrent, t: (k: string, o?: Record<string, unknown>) => string): string {
  const hours = m.workingHours ? Object.entries(m.workingHours).filter(([, v]) => v && v.intervals.length).map(([d, v]) => `${t(`config:onboarding.wizard.dow.${DOW[Number(d)]}`)} ${v!.intervals.map((i) => `${i.start}–${i.end}`).join(', ')}`).join('; ') : '';
  return [
    [m.email, m.role ? t(`config:onboarding.wizard.roles.${m.role}`, { defaultValue: m.role }) : ''].filter(Boolean).join(' · '),
    m.services?.length ? `${t('config:onboarding.wizard.fields.services')}: ${m.services.join(', ')}` : '',
    hours ? `${t('config:onboarding.wizard.fields.workingHours')}: ${hours}` : '',
    m.canBeSubstitute === false ? t('config:onboarding.wizard.noSubstitute') : '',
  ].filter(Boolean).join('\n');
}

function Part({ id, label, parts, setParts, applied, children }: { id: string; label: string; parts: string[]; setParts: (p: string[]) => void; applied: string[]; children: ReactNode }) {
  const { t } = useTranslation(['config']);
  const done = applied.includes(id);
  return (
    <section className="swz__part">
      <div className="k-row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <strong>{label}</strong>
        {done ? <Chip tone="accent">{t('config:onboarding.wizard.done')}</Chip> : <Switch label={t('config:onboarding.wizard.include')} checked={parts.includes(id)} onChange={(v) => setParts(v ? [...parts, id] : parts.filter((x) => x !== id))} />}
      </div>
      <div className="k-stack" style={{ gap: 10, marginTop: 10 }}>{children}</div>
    </section>
  );
}

function Diff({ label, before, after, long }: { label: string; before?: string | null; after: string; long?: boolean }) {
  const { t } = useTranslation(['config']);
  const same = (before ?? '') === after;
  return (
    <div className={`swz__diff ${long ? 'swz__diff--long' : ''}`}>
      <div className="swz__diff-label">{label}{same && <span className="k-muted"> · {t('config:onboarding.wizard.unchanged')}</span>}</div>
      {!same && before !== undefined && before !== null && before !== '' && before !== '—' && <div className="swz__before">{before}</div>}
      <div className={same ? 'swz__same' : 'swz__after'}>{after || '—'}</div>
    </div>
  );
}

function ListDiff({ label, before, after }: { label: string; before: string[]; after: string[] }) {
  const { t } = useTranslation(['config']);
  const removed = before.filter((x) => !after.includes(x));
  return (
    <div className="swz__diff swz__diff--long">
      <div className="swz__diff-label">{label} <span className="k-muted">· {t('config:onboarding.wizard.itemsCount', { before: before.length, after: after.length })}</span></div>
      <ul className="swz__list">
        {after.map((x, i) => <li key={i} className={before.includes(x) ? 'swz__same' : 'swz__after'}>{x}</li>)}
        {removed.map((x, i) => <li key={`r${i}`} className="swz__before">{x}</li>)}
      </ul>
    </div>
  );
}
